/**
 * Spec Phase 11 Slice 11E — keyboard shortcut cheatsheet modal.
 *
 * Triggered by ``?`` (the universal cheatsheet shortcut). The
 * Cmd / Ctrl symbol auto-detects the platform via
 * navigator.platform — Mac drafters see ⌘, the rest see Ctrl.
 */
import React, { useEffect } from 'react';
import { Keyboard, X } from 'lucide-react';

interface Props {
  onClose: () => void;
}


function _modKey(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /mac|ipad|iphone|ipod/i.test(navigator.platform) ? '⌘' : 'Ctrl';
}


const _SHORTCUTS: Array<[string, string]> = [
  ['MOD + Enter',         'Submit AI prompt bar (NL edit)'],
  ['MOD + G',             'Generate active section'],
  ['MOD + Shift + G',     'Generate everything'],
  ['MOD + →',             'Next section'],
  ['MOD + ←',             'Previous section'],
  ['MOD + S',             'Force save (flush offline queue)'],
  ['MOD + B',             'Toggle left rail'],
  ['MOD + Shift + B',     'Toggle right rail'],
  ['MOD + K',             'Focus AI prompt bar'],
  ['?',                   'Open this cheatsheet'],
  ['Esc',                 'Close any open modal'],
];


export default function ShortcutCheatsheet({ onClose }: Props): JSX.Element {
  // Esc closes the cheatsheet (matches every other spec modal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mod = _modKey();

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/40 px-4"
      role="dialog"
      aria-modal="true"
      data-testid="spec-shortcut-cheatsheet"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-md border border-zinc-300 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2">
          <h3 className="flex items-center gap-2 text-sm font-medium text-zinc-800">
            <Keyboard className="h-4 w-4" />
            Keyboard shortcuts
          </h3>
          <button
            type="button"
            className="rounded-sm p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            onClick={onClose}
            aria-label="Close cheatsheet"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-4 py-3">
          <p className="mb-2 text-[11px] text-zinc-500">
            Shortcuts apply when the spec editor is focused. Drafters
            typing into right-rail forms get native browser behaviour
            (e.g. Cmd+S falls back to "Save Page").
          </p>
          <table className="w-full text-xs">
            <tbody>
              {_SHORTCUTS.map(([combo, desc]) => (
                <tr key={combo} className="border-b border-zinc-100 last:border-b-0">
                  <td className="py-1.5 pr-3 font-mono text-[11px] text-zinc-700 whitespace-nowrap">
                    {combo.replace(/MOD/g, mod)}
                  </td>
                  <td className="py-1.5 text-zinc-600">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
